// covered-by: frontends/carrie/src/ask-v2/review/__tests__/useLoadedReviewCommentGroups.test.ts
import { useMemo, useRef } from "react";

import {
  type LoadedReviewCommentGroups,
  type ReviewCommentVisibilityFields,
  withLoadedReviewCommentGroups,
} from "../../utils/visibleReviewComments";

const NOTHING_LOADED: LoadedReviewCommentGroups = new Map();

/**
 * The section each finding sat in when the review loaded, held for the visit.
 *
 * why a snapshot and not `reviewCommentGroup` on every render: the numbers the
 * list and the pins share are counted in this order, so a judgment made in
 * this session renumbers no finding under her hand. The list's sections and
 * counts do NOT read it -- they follow the judgment at once -- so a section
 * can show 2, 5, 6 until the next load numbers it contiguously again.
 *
 * `loadKey` is the review the comments belong to: a new one (another version)
 * is a new load. With no key the snapshot lives as long as the caller.
 */
export function useLoadedReviewCommentGroups(
  comments:
    | readonly (ReviewCommentVisibilityFields & { _id: string })[]
    | undefined,
  loadKey?: string | null,
): LoadedReviewCommentGroups {
  const snapshot = useRef<{
    loadKey: string | null | undefined;
    groups: LoadedReviewCommentGroups;
  }>({ loadKey, groups: NOTHING_LOADED });
  return useMemo(() => {
    const kept =
      snapshot.current.loadKey === loadKey
        ? snapshot.current.groups
        : NOTHING_LOADED;
    const groups = withLoadedReviewCommentGroups(kept, comments ?? []);
    snapshot.current = { loadKey, groups };
    return groups;
  }, [comments, loadKey]);
}
